// conv_archive.ts — Capa 4 del rediseño v016.
//
// Cada usuario tiene un "archive" cifrado server-side por conversación. Garantiza
// invariante I2: cualquier device nuevo del usuario, al loguear, descarga el archive
// y reconstruye TODO el historial sin depender de blobs efímeros.
//
// Modelo:
//  - master_vault_key   = wrappingKey actual (Argon2id del password) durante v110-v119
//                         hasta que se introduzca BIP39 master.
//  - conversation_tag   = HMAC(master_vault_key, sorted(realA, realB) || freqLabel)
//                         opaco al server, mismo en todos los devices del usuario.
//  - conv_archive_key   = HKDF(master_vault_key, peerSyphraxId || freqLabel)  (32B)
//  - chunk ciphertext   = secretbox(JSON.stringify(payload), conv_archive_key)

import * as crypto from '../crypto';
import { api } from '../api';

export interface ArchivePayload {
  messageId: string;
  conversationKey: { peerSyphraxId: string; freqLabel: string };
  fromMe: boolean;
  fromDeviceId: string | null;     // device del sender (null en mensajes míos via self-copy ya inserted localmente)
  kind: 'msg' | 'media' | 'ctrl';
  body: string;                     // texto plano o JSON serializado del media
  createdAt: string;                // ISO
  // Estados al momento de escribir el chunk. Pueden actualizarse vía edit chunks.
  delivered?: boolean;
  read?: boolean;
  // Operaciones especiales: edit, tombstone, reaction. Para v110 se ignoran (sólo 'append').
  op?: 'append' | 'edit' | 'tombstone';
  targetMessageId?: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export function deriveConversationTag(
  masterVaultKey: Uint8Array,
  mySyphraxId: string,
  peerSyphraxId: string,
  freqLabel: string,
): Uint8Array {
  const a = mySyphraxId < peerSyphraxId ? mySyphraxId : peerSyphraxId;
  const b = mySyphraxId < peerSyphraxId ? peerSyphraxId : mySyphraxId;
  const ikm = enc.encode('syphrax-conv-tag-v1::' + a + '::' + b + '::' + freqLabel);
  // HMAC-SHA512 truncado a 32 bytes.
  const tag = crypto.hmacSha512(masterVaultKey, ikm);
  return tag.slice(0, 32);
}

// Una sola archive_master_key por usuario. Permite descifrar cualquier chunk
// sin necesidad de saber a priori el peer (el peer va dentro del payload).
// Si master_vault_key se compromete, todo el archive cae — pero en ese caso
// tambien caen los mensajes en transito y las identidades de devices (mismo blast radius).
export function deriveArchiveMasterKey(masterVaultKey: Uint8Array): Uint8Array {
  const info = enc.encode('syphrax-archive-master-v1');
  const salt = new Uint8Array(0);
  return crypto.hkdf(masterVaultKey, salt, info, 32);
}

export function encryptArchiveChunk(payload: ArchivePayload, key: Uint8Array): Uint8Array {
  const json = JSON.stringify(payload);
  // wrapPrivateKey devuelve {ciphertext, nonce} donde ciphertext YA contiene
  // nonce || cipher concatenados. El bug de v016 duplicaba el nonce al
  // re-concatenarlo aquí; decryptArchiveChunk solo lo extraía una vez ⇒ siempre fallaba.
  const wrapped = crypto.wrapPrivateKey(enc.encode(json), key);
  return wrapped.ciphertext;
}

export function decryptArchiveChunk(blob: Uint8Array, key: Uint8Array): ArchivePayload | null {
  try {
    const nonceLen = 24;  // xsalsa20poly1305
    if (blob.length < nonceLen + 16) return null;
    const nonce = blob.slice(0, nonceLen);
    const ciphertext = blob.slice(nonceLen);
    // Reconstruimos el blob format que espera unwrapPrivateKey.
    const combined = new Uint8Array(nonce.length + ciphertext.length);
    combined.set(nonce, 0);
    combined.set(ciphertext, nonce.length);
    const plain = crypto.unwrapPrivateKey(combined, key);
    const json = dec.decode(plain);
    return JSON.parse(json) as ArchivePayload;
  } catch (_e) {
    return null;
  }
}

// Escribe un chunk al server. Idempotente: si el chunk con ese (account, conv_tag, message_id)
// ya existe, server devuelve 200 con deduplicated=true y nosotros no hacemos nada.
// Devuelve true si se escribió/dedupó OK, false si falló (network).
export async function writeArchiveChunk(
  masterVaultKey: Uint8Array,
  mySyphraxId: string,
  peerSyphraxId: string,
  freqLabel: string,
  payload: ArchivePayload,
): Promise<boolean> {
  try {
    const tag = deriveConversationTag(masterVaultKey, mySyphraxId, peerSyphraxId, freqLabel);
    const key = deriveArchiveMasterKey(masterVaultKey);
    const blob = encryptArchiveChunk(payload, key);
    await api.putArchiveChunk({
      conversation_tag: crypto.b64encode(tag),
      message_id: payload.messageId,
      ciphertext: crypto.b64encode(blob),
    });
    return true;
  } catch (err) {
    console.warn('[archive] write failed:', (err as Error).message);
    return false;
  }
}

// Actualiza los flags (read/delivered) de UN chunk ya existente.
// Re-escribe el chunk completo con el payload modificado. El backend hace UPSERT
// (sobreescribe ciphertext si el messageId ya existe). Idempotente.
// Devuelve true si encontró el chunk y lo re-escribió, false si no existía.
export async function updateChunkFlags(
  masterVaultKey: Uint8Array,
  mySyphraxId: string,
  peerSyphraxId: string,
  freqLabel: string,
  messageId: string,
  flags: { delivered?: boolean; read?: boolean },
): Promise<boolean> {
  try {
    const tag = deriveConversationTag(masterVaultKey, mySyphraxId, peerSyphraxId, freqLabel);
    const key = deriveArchiveMasterKey(masterVaultKey);
    const r = await api.getArchiveChunks({ conversation_tag: crypto.b64encode(tag), limit: 500 });
    for (const c of r.chunks) {
      if (c.message_id !== messageId) continue;
      const payload = decryptArchiveChunk(crypto.b64decode(c.ciphertext), key);
      if (!payload) return false;
      // Monotónico: una vez true, jamás puede volver a false.
      const updated: ArchivePayload = {
        ...payload,
        delivered: payload.delivered === true || flags.delivered === true,
        read: payload.read === true || flags.read === true,
      };
      const blob = encryptArchiveChunk(updated, key);
      // Endpoint UPDATE: sólo modifica un chunk existente. No puede crearlo.
      await api.putArchiveChunkFlags({
        conversation_tag: crypto.b64encode(tag),
        message_id: messageId,
        ciphertext: crypto.b64encode(blob),
      });
      return true;
    }
    return false;
  } catch (err) {
    console.warn('[archive] updateChunkFlags failed:', (err as Error).message);
    return false;
  }
}

// Hidrata UNA conversación: descarga chunks paginados (más recientes primero) y los descifra.
// limit por página, before opcional para paginar hacia atrás.
export interface HydratedChunk {
  messageId: string;
  payload: ArchivePayload;
  serverCreatedAt: string;
}

export async function hydrateConversation(
  masterVaultKey: Uint8Array,
  mySyphraxId: string,
  peerSyphraxId: string,
  freqLabel: string,
  opts: { limit?: number; before?: string } = {},
): Promise<HydratedChunk[]> {
  const tag = deriveConversationTag(masterVaultKey, mySyphraxId, peerSyphraxId, freqLabel);
  const key = deriveArchiveMasterKey(masterVaultKey);
  const r = await api.getArchiveChunks({
    conversation_tag: crypto.b64encode(tag),
    limit: opts.limit ?? 200,
    before: opts.before,
  });
  const out: HydratedChunk[] = [];
  for (const c of r.chunks) {
    const blob = crypto.b64decode(c.ciphertext);
    const payload = decryptArchiveChunk(blob, key);
    if (!payload) continue;  // chunk corrupto o de otra clave: skip
    out.push({ messageId: c.message_id, payload, serverCreatedAt: c.created_at });
  }
  return out;
}

// Lista todas las conversaciones del account autenticado. Devuelve los conv_tags
// (opacos) con counts. El cliente debe mapear cada tag a un peer conocido para
// poder mostrar nombres. Si el tag no se puede mapear (peer aún desconocido),
// la conversación queda "oculta" hasta que el cliente recupere el contexto vía
// algún mensaje recibido o vía libreta de contactos.
export async function listConversations(): Promise<Array<{ tag: string; count: number; lastAt: string; firstAt: string }>> {
  const r = await api.listArchiveConversations();
  return r.conversations.map((c) => ({
    tag: c.conversation_tag,
    count: c.count,
    lastAt: c.last_created_at,
    firstAt: c.first_created_at,
  }));
}


// Hidrata TODAS las conversaciones del archive del account autenticado.
// Devuelve los chunks descifrados agrupados por (peerSyphraxId, freqLabel),
// que el cliente extrae del payload interior. Usar al loguear un device nuevo.
export interface HydratedAll {
  byConversation: Map<string, { peerSyphraxId: string; freqLabel: string; chunks: HydratedChunk[] }>;
}

export async function hydrateAllConversations(
  masterVaultKey: Uint8Array,
  opts: { limitPerConv?: number } = {},
): Promise<HydratedAll> {
  const key = deriveArchiveMasterKey(masterVaultKey);
  const conversations = await listConversations();
  const byConversation = new Map<string, { peerSyphraxId: string; freqLabel: string; chunks: HydratedChunk[] }>();
  const limit = opts.limitPerConv ?? 200;

  for (const conv of conversations) {
    try {
      const r = await api.getArchiveChunks({
        conversation_tag: conv.tag,
        limit,
      });
      const decrypted: HydratedChunk[] = [];
      let peerSyphraxId = '';
      let freqLabel = '';
      for (const c of r.chunks) {
        const blob = crypto.b64decode(c.ciphertext);
        const payload = decryptArchiveChunk(blob, key);
        if (!payload) continue;
        decrypted.push({ messageId: c.message_id, payload, serverCreatedAt: c.created_at });
        peerSyphraxId = payload.conversationKey.peerSyphraxId;
        freqLabel = payload.conversationKey.freqLabel;
      }
      if (decrypted.length > 0 && peerSyphraxId) {
        const groupKey = peerSyphraxId + '::' + freqLabel;
        byConversation.set(groupKey, { peerSyphraxId, freqLabel, chunks: decrypted });
      }
    } catch (err) {
      console.warn('[archive] hydrate failed for conv', conv.tag, (err as Error).message);
    }
  }
  return { byConversation };
}
